import type { Language2 } from "./schemas/language";

/** Build-time defaults for the dev environment. Exported so tooling (the
 * bub-feature-flags admin UI) can enumerate every known flag. */
export const devFlags = {
  missingProfileDataModal: true,
  /** When false, registration rejects a phone number already on an active
   * account. Missing or true keeps the current behaviour (duplicates allowed).
   * Numbers on the duplicate-phone allowlist in global settings are exempt. */
  allowDuplicatePhoneNumbers: true,
  /** Logged-in SMS phone verification (mobile/my.bubbles profile + admin verified badge).
   * Missing or false keeps freely editable phone numbers and no OTP. */
  phoneVerification: false,
  /** Welcome coupon for verifying a phone (needs phoneVerification too): backend
   * grant + mobile welcome screen, shop-list card and "number won't earn it" warnings.
   * Missing or false issues no coupon and shows none of it. */
  welcomeCoupon: false,
  /** When true, app / my.bubbles / SMS door opening requires a verified phone.
   * Missing or false keeps door opening independent of phone verification. */
  requireVerifiedPhoneForDoor: false,
  /** "Every X-th start" shop campaigns: admin page, the hold grant on the machines
   * page / after kiosk login, the floating campaign banner and discounted prices.
   * The backend also refuses holds unless the `nthPurchaseCampaign` row is on (a
   * `shop:<id>` row can enable one shop). Starts are counted either way. */
  nthPurchaseCampaign: false,
  kiosk: {
    cashPaymentDebug: false,
    machineStatusIcons: "new",
    recyclerFillEvents: false,
    stickyKeyboard: false,
    startScreenSyncLoading: true,
    driverUrl: "https://localhost:8080",
    language: "hu" as Language2,
    sessionTracking: true,
    supportPhone: "+36-1-506-0427",
    /** Classify kiosk bank-card failures (declined / comms / unreachable). Off keeps 3061 / payment_error. */
    classifiedTerminalErrors: false,
    /** Email one-time code during registration instead of an activation link. */
    emailVerificationCode: false,
    /** List the logged-in user's visible, applicable coupons on the coupon screen. */
    myCoupons: false,
    /** Picture-based accepted coins/notes display on the cash screens, without the
     * cash warning modal on the payment selector. Off keeps the old text banner + modal. */
    newCashUi: true,
    /** Spoken audio clips per screen/modal (uploaded in the admin "Kioszk hangok" page)
     * and the mute button. Off shows no audio UI and plays nothing; enable per shop
     * with a `shop:<id>` row. cash-agent downloads the clips either way. */
    audio: false,
    /** After a single washer purchase, the step-by-step "start your machine" guide
     * that polls GET /orders/:id/status until the machine spins (or 3 minutes pass).
     * Off keeps the invoice question + "Köszönjük" screens. Dryers always keep them. */
    washStartGuide: true,
  },
  mobile: {
    machineStatusIcons: "new",
    /** Shop details "Open door" button (also overridable via /feature-flags). */
    doorOpenerButton: false,
    /** SMS / phone door-open option on the door screen. */
    doorSms: false,
    /** Email one-time code during registration instead of an activation link. */
    emailVerificationCode: false,
    /** "My coupons" drawer entry + applicable-coupon list in the coupon modal. */
    myCoupons: false,
  },
  myBubbles: {
    /** Shop details "Open door" button (also overridable via /feature-flags). */
    doorOpenerButton: true,
    /** SMS / phone door-open option on the door screen. */
    doorSms: false,
    /** Email one-time code during registration instead of an activation link.
     * Backend send/check/register follow the requesting app's matching flag
     * (myBubbles / mobile / kiosk), so each client can be toggled separately. */
    emailVerificationCode: false,
    /** "My coupons" sidebar page + applicable-coupon list on the payment page. */
    myCoupons: false,
    /** Washer / dryer start guide: door + program / temperature before paying,
     * then waits for the drum to spin (like kiosk.washStartGuide). */
    washStartGuide: true,
  },
  admin: {
    remoteController: false,
    /** "Support jegyek" sidebar page. */
    supportTickets: true,
    collectorsDiscountDashboard: true,
    shopGroups: false,
    machineCyclePricing: "new",
    storeSettings: "new",
    pricingRules: "old",
    regionsPage: "new",
    reportsPage: "new",
    loggerPage: "new",
    sessionsPage: "new",
    moneyQuery: "new",
    manualPayout: "new",
    transactionDetail: "new",
    /** Admin UI to view/print the customer door-access QR for a shop. */
    doorAccessQr: false,
    /** Customer door-opens page/tab and SMS door-open blacklist. */
    customerDoorOpens: false,
    /** "Tiltások" shop-page tab: per-shop door bans (users + SMS numbers) for
     * admins, support and franchise partners. */
    shopDoorBans: true,
    /** "Kioszk hangok" page: upload the kiosk audio clips (Szuperadmin only). */
    kioskAudio: true,
    /** Shop-support "Kiosk hangszóró" card: talk live from the admin's mic to the
     * kiosk speaker via go2rtc. Only shown for shops with the "speakers" kiosk setting.
     * The backend refuses /kiosk-talk sessions while this is off. */
    kioskTalk: true,
  },
} as const;

/** Build-time defaults for the prod environment. Exported so tooling (the
 * bub-feature-flags admin UI) can enumerate every known flag. */
export const prodFlags = {
  missingProfileDataModal: true,
  /** When false, registration rejects a phone number already on an active
   * account. Missing or true keeps the current behaviour (duplicates allowed).
   * Numbers on the duplicate-phone allowlist in global settings are exempt. */
  allowDuplicatePhoneNumbers: true,
  /** Logged-in SMS phone verification (mobile/my.bubbles profile + admin verified badge).
   * Missing or false keeps freely editable phone numbers and no OTP. */
  phoneVerification: false,
  /** Welcome coupon for verifying a phone (needs phoneVerification too): backend
   * grant + mobile welcome screen, shop-list card and "number won't earn it" warnings.
   * Missing or false issues no coupon and shows none of it. */
  welcomeCoupon: false,
  /** When true, app / my.bubbles / SMS door opening requires a verified phone.
   * Missing or false keeps door opening independent of phone verification. */
  requireVerifiedPhoneForDoor: false,
  /** "Every X-th start" shop campaigns: admin page, the hold grant on the machines
   * page / after kiosk login, the floating campaign banner and discounted prices.
   * The backend also refuses holds unless the `nthPurchaseCampaign` row is on (a
   * `shop:<id>` row can enable one shop). Starts are counted either way. */
  nthPurchaseCampaign: false,
  kiosk: {
    cashPaymentDebug: false,
    machineStatusIcons: "new",
    recyclerFillEvents: false,
    stickyKeyboard: false,
    startScreenSyncLoading: true,
    driverUrl: "https://localhost:8080",
    sessionTracking: true,
    supportPhone: "+36-1-506-0427",
    /** Classify kiosk bank-card failures (declined / comms / unreachable). Off keeps 3061 / payment_error. */
    classifiedTerminalErrors: false,
    /** Email one-time code during registration instead of an activation link. */
    emailVerificationCode: false,
    /** List the logged-in user's visible, applicable coupons on the coupon screen. */
    myCoupons: false,
    /** Picture-based accepted coins/notes display on the cash screens, without the
     * cash warning modal on the payment selector. Off keeps the old text banner + modal. */
    newCashUi: false,
    /** Spoken audio clips per screen/modal (uploaded in the admin "Kioszk hangok" page)
     * and the mute button. Off shows no audio UI and plays nothing; enable per shop
     * with a `shop:<id>` row. cash-agent downloads the clips either way. */
    audio: false,
    /** After a single washer purchase, the step-by-step "start your machine" guide
     * that polls GET /orders/:id/status until the machine spins (or 3 minutes pass).
     * Off keeps the invoice question + "Köszönjük" screens. Dryers always keep them. */
    washStartGuide: false,
  },
  mobile: {
    machineStatusIcons: "new",
    /** Shop details "Open door" button (also overridable via /feature-flags). */
    doorOpenerButton: false,
    /** SMS / phone door-open option on the door screen. */
    doorSms: false,
    /** Email one-time code during registration instead of an activation link. */
    emailVerificationCode: false,
    /** "My coupons" drawer entry + applicable-coupon list in the coupon modal. */
    myCoupons: false,
  },
  myBubbles: {
    /** Shop details "Open door" button (also overridable via /feature-flags). */
    doorOpenerButton: true,
    /** SMS / phone door-open option on the door screen. */
    doorSms: false,
    /** Email one-time code during registration instead of an activation link.
     * Backend send/check/register follow the requesting app's matching flag
     * (myBubbles / mobile / kiosk), so each client can be toggled separately. */
    emailVerificationCode: false,
    /** "My coupons" sidebar page + applicable-coupon list on the payment page. */
    myCoupons: false,
    /** Washer / dryer start guide: door + program / temperature before paying,
     * then waits for the drum to spin (like kiosk.washStartGuide). */
    washStartGuide: false,
  },
  admin: {
    remoteController: false,
    /** "Support jegyek" sidebar page. */
    supportTickets: false,
    collectorsDiscountDashboard: true,
    shopGroups: false,
    machineCyclePricing: "old",
    storeSettings: "old",
    pricingRules: "old",
    regionsPage: "old",
    reportsPage: "old",
    loggerPage: "old",
    sessionsPage: "old",
    moneyQuery: "old",
    manualPayout: "old",
    transactionDetail: "old",
    /** Admin UI to view/print the customer door-access QR for a shop. */
    doorAccessQr: false,
    /** Customer door-opens page/tab and SMS door-open blacklist. */
    customerDoorOpens: false,
    /** "Tiltások" shop-page tab: per-shop door bans (users + SMS numbers) for
     * admins, support and franchise partners. */
    shopDoorBans: false,
    /** "Kioszk hangok" page: upload the kiosk audio clips (Szuperadmin only). */
    kioskAudio: false,
    /** Shop-support "Kiosk hangszóró" card: talk live from the admin's mic to the
     * kiosk speaker via go2rtc. Only shown for shops with the "speakers" kiosk setting.
     * The backend refuses /kiosk-talk sessions while this is off. */
    kioskTalk: true,
  },
} as const;

// Overrides fetched from the backend at runtime, keyed by dotted flag name
// (e.g. "admin.shopGroups"). They win over the build-time values above.
const dynamicOverrides: Record<string, unknown> = {};

export function applyDynamicFeatureFlags(flags: Record<string, unknown>) {
  Object.assign(dynamicOverrides, flags);
}

/** Drop every override, then apply `flags`. Use when the whole set comes from
 * one server response, so a flag the server no longer sends falls back to its
 * build-time value instead of lingering from an earlier fetch (e.g. after the
 * mobile app is pointed at a different backend). */
export function replaceDynamicFeatureFlags(flags: Record<string, unknown>) {
  for (const key of Object.keys(dynamicOverrides)) {
    delete dynamicOverrides[key];
  }
  Object.assign(dynamicOverrides, flags);
}

/**
 * Fetch flag overrides from bb-elysia-server and apply them, replacing any
 * overrides from a previous call. Call before app bootstrap (and again later to
 * refresh); on failure the overrides already in effect stay untouched, so a
 * fresh app keeps the build-time flags below. Resolves with the fetched flags.
 */
export async function loadDynamicFeatureFlags(
  serverUrl: string,
  origin: string,
  timeoutMs: number = 5000,
): Promise<Record<string, unknown>> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(
      `${serverUrl.replace(/\/$/, "")}/feature-flags?origin=${encodeURIComponent(origin)}`,
      { signal: controller.signal },
    );
    if (!response.ok) {
      throw new Error(`Failed to load feature flags: ${response.status}`);
    }
    const data = (await response.json()) as { flags: Record<string, unknown> };
    replaceDynamicFeatureFlags(data.flags);
    return data.flags;
  } finally {
    clearTimeout(timeout);
  }
}

function getFlagEnv(): string | undefined {
  // Explicit override (e.g. set by Angular before module loads)
  if (typeof globalThis !== "undefined" && (globalThis as any).__FLAG_ENV__) {
    return (globalThis as any).__FLAG_ENV__;
  }
  return "prod";
}

function resolveFlags() {
  return getFlagEnv() === "prod" ? prodFlags : devFlags;
}

// Object values are wrapped recursively so dynamic overrides resolve at any
// depth, keyed by dotted path (e.g. "admin.shopGroups"). Proxies are cached
// per path so repeated accesses return the same reference.
const flagProxyCache = new Map<string, unknown>();

function resolveFlagValue(path: string, staticValue: unknown): unknown {
  if (typeof staticValue !== "object" || staticValue === null) {
    const override = dynamicOverrides[path];
    return override === undefined ? staticValue : override;
  }
  const cached = flagProxyCache.get(path);
  if (cached !== undefined) {
    return cached;
  }
  const proxy = new Proxy(staticValue, {
    get(target, key) {
      return resolveFlagValue(path + "." + String(key), (target as any)[key]);
    },
  });
  flagProxyCache.set(path, proxy);
  return proxy;
}

// Lazy proxy so that globalThis.__FLAG_ENV__ can be set before first access.
// Dynamic overrides loaded from the backend win over the build-time values.
const flags = new Proxy({} as typeof devFlags, {
  get(_, key) {
    return resolveFlagValue(String(key), (resolveFlags() as any)[key]);
  },
});

type Flags = typeof devFlags;
type FlagValue<T> = T extends "old" | "new"
  ? "old" | "new"
  : T extends Language2
    ? Language2
    : T extends string
      ? string
      : boolean;

export type FeatureFlags = Prettify<{
  [key1 in keyof Flags]: Flags[key1] extends object
    ? { [key in keyof Flags[key1]]: FlagValue<Flags[key1][key]> }
    : FlagValue<Flags[key1]>;
}>;

export type Prettify<T> = {
  [K in keyof T]: T[K];
} & {};

export const featureFlags = flags as FeatureFlags;

/** Plain copy of `value` with every dynamic override under `path` applied. */
function snapshotFlagValue(path: string, value: unknown): unknown {
  if (typeof value !== "object" || value === null) {
    return dynamicOverrides[path] ?? value;
  }
  const keys = new Set(Object.keys(value));
  // overrides for flags that have no build-time value
  for (const key of Object.keys(dynamicOverrides)) {
    if (key.startsWith(path + ".")) keys.add(key.slice(path.length + 1).split(".")[0]!);
  }
  return Object.fromEntries(
    [...keys].map((key) => [key, snapshotFlagValue(`${path}.${key}`, (value as any)[key])]),
  );
}

/**
 * Every flag as the app sees it right now (build-time values of the current
 * env + the backend's overrides), as a plain object. `$env` is the build-time
 * set in use, `$overrides` what the backend sent.
 */
export function featureFlagsSnapshot() {
  const env = resolveFlags() as Record<string, unknown>;
  const keys = new Set([...Object.keys(env), ...Object.keys(dynamicOverrides).map((key) => key.split(".")[0]!)]);
  return {
    $env: getFlagEnv() === "prod" ? ("prod" as const) : ("dev" as const),
    $overrides: { ...dynamicOverrides },
    ...(Object.fromEntries([...keys].map((key) => [key, snapshotFlagValue(key, env[key])])) as FeatureFlags),
  };
}

// For debugging from the browser console: `featureFlags` shows the current
// values. A getter, so it's never stale after the overrides load.
if ("window" in globalThis) {
  Object.defineProperty(globalThis, "featureFlags", {
    get: featureFlagsSnapshot,
    configurable: true,
  });
}
